import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Category } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  useListOrganizationCategories,
  useUpdateOrganizationCategory,
} from "~/api/organization/organization";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useOrganizationParams } from "~/helpers";
import { ColorPicker } from "./ColorPicker";
import { DeleteCategoryAlertDialog } from "./DeleteCategoryAlertDialog";
import EditCategoryModal from "./EditCategoryModal";
import MoveCategoryToFolderModal from "./MoveCategoryToFolderModal";
import { ViewCategoryDialog } from "./ViewCategoryModal";

interface CategoryListItemProps {
  category: Category;
}

export const CategoryListItem: FC<CategoryListItemProps> = ({ category }) => {
  const { organizationId } = useOrganizationParams();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMoveToFolderOpen, setIsMoveToFolderOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { mutateAsync: updateCategory } = useUpdateOrganizationCategory();
  const { data: categories } = useListOrganizationCategories(organizationId);
  const queryClient = useQueryClient();
  const intl = useIntl();

  const fallbackCategory = categories?.find((c) => c.isFallback) || null;

  const handleUpdateCategory = async (color: string) => {
    await updateCategory({
      organizationId: organizationId,
      categoryId: category.id.toString(),
      data: { color },
    });
    queryClient.invalidateQueries({
      queryKey: getListOrganizationCategoriesQueryKey(organizationId),
    });
  };

  const dropdownMenuItems = [
    {
      label: intl.formatMessage({ id: "categoryListItem.edit" }),
      onClick: () => {
        setIsEditModalOpen(true);
        setIsDropdownOpen(false);
      },
    },
    {
      label: intl.formatMessage({ id: "categoryListItem.moveToFolder" }),
      onClick: () => {
        setIsMoveToFolderOpen(true);
        setIsDropdownOpen(false);
      },
    },
    {
      label: intl.formatMessage({ id: "categoryListItem.delete" }),
      onClick: () => {
        setIsDeleteDialogOpen(true);
        setIsDropdownOpen(false);
      },
      className: "text-destructive",
    },
  ];

  const handleCardClick = () => {
    setIsExpanded(true);
  };

  return (
    <>
      <Card
        className="shadow-none rounded-[8px] group hover:bg-accent/50 cursor-pointer transition-colors duration-200"
        onClick={handleCardClick}
      >
        <CardHeader className="pt-[14px] px-[16px] pb-0">
          <div className="flex items-start justify-between">
            <div className="flex gap-3 items-center">
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex flex-col items-center"
              >
                <ColorPicker
                  value={category.color}
                  onChange={handleUpdateCategory}
                  categoryId={category.id}
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-lg leading-6">
                    {category.name}
                  </h3>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  open={isDropdownOpen}
                  onOpenChange={setIsDropdownOpen}
                >
                  <DropdownMenuTrigger asChild disabled={category.isFallback}>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      disabled={category.isFallback}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {dropdownMenuItems.map(({ label, onClick, className }) => (
                      <DropdownMenuItem
                        key={label}
                        onClick={onClick}
                        className={className}
                      >
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-[16px]">
          <div className="mb-[16px]">
            <p className="text-sm text-muted-foreground">
              {category.definition}
            </p>
          </div>
          <div className="mt-[12px]">
            <div>
              <h4 className="text-sm text-muted-foreground mb-1">
                <FormattedMessage id="categoryListItem.namingConventions" />
              </h4>
              <div className="flex gap-2 text-sm mt-[4px]">
                {category.namingConvention}
              </div>
              <div>
                <div className="flex items-center gap-2 mt-[12px]">
                  <h4 className="text-sm text-muted-foreground">
                    <FormattedMessage id="categoryListItem.namingExamples" />
                  </h4>
                </div>
                <div className="mt-2">
                  <p className="text-sm truncate">{category.namingExample}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {isDeleteDialogOpen && (
        <DeleteCategoryAlertDialog
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          categoryId={category.id.toString()}
          fallbackCategory={fallbackCategory}
        />
      )}
      {isEditModalOpen && (
        <EditCategoryModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          category={category}
        />
      )}
      {isMoveToFolderOpen && (
        <MoveCategoryToFolderModal
          open={isMoveToFolderOpen}
          onOpenChange={setIsMoveToFolderOpen}
          category={category}
        />
      )}
      {isExpanded && (
        <ViewCategoryDialog
          category={category}
          open={isExpanded}
          onOpenChange={setIsExpanded}
          onEdit={() => {
            setIsExpanded(false);
            setIsEditModalOpen(true);
          }}
        />
      )}
    </>
  );
};
